import React, { useState } from 'react';
import { Loader2, Mail, ArrowRight, ArrowLeft } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { useUserStore } from '../../stores/userStore';
import { MARKETING_URL } from '../../constants/onboarding';
import { useTurnstile } from './useTurnstile';

/**
 * The sign-in panel, rendered INLINE by `LandingPage`.
 *
 * ⚠️ THIS WAS `AuthModal`, AND IT WAS A MODAL OVER NOTHING. `LandingPage`
 * mounted it with `isOpen` hardcoded to `true` and an `onClose` that hid the
 * only thing on the page, so the backdrop dimmed an empty screen and the ✕
 * closed the app into a blank void. It is the page's content, so it is now a
 * panel — no backdrop, no dismiss.
 *
 * ⚠️⚠️ EVERY BEHAVIOUR OF THE OLD MODAL IS PRESERVED, including the two easy to
 * miss: the email-confirmation screen that `signUp` triggers via
 * `pendingConfirmationEmail`, and its resend. The signup funnel was proved end
 * to end on 2026-08-31 and runs straight through both.
 */

/** ⚠️ `signup` is NOT dead. See the note on the mode switch below. */
type Mode = 'signin' | 'signup';

const EARLY_ACCESS_URL = `${MARKETING_URL}/early-access`;

export function AuthPanel() {
  const [mode, setMode] = useState<Mode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendState, setResendState] = useState<'idle' | 'sending' | 'sent'>('idle');

  const {
    signIn, signUp, pendingConfirmationEmail, resendConfirmation, clearPendingConfirmation,
  } = useAuthStore();
  const { setName } = useUserStore();
  // ⭐ Every Supabase auth request carries a Turnstile token (see useTurnstile).
  const captcha = useTurnstile(pendingConfirmationEmail ? 'resend' : 'form');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      if (mode === 'signup') {
        if (!username.trim()) throw new Error('Username is required');
        await signUp(email, password, username, captcha.token ?? undefined);
        setName(username.trim());
        /* ⚠️ No close call here, unlike the old modal. Confirmation is required,
           so `signUp` sets `pendingConfirmationEmail` and the screen below takes
           over; when it is not required the auth listener swaps the whole page. */
      } else {
        await signIn(email, password, captcha.token ?? undefined);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Authentication failed');
    } finally {
      setLoading(false);
      captcha.reset(); // a token is single-use
    }
  };

  const handleResend = async () => {
    if (!pendingConfirmationEmail) return;
    setError(null);
    setResendState('sending');
    try {
      await resendConfirmation(pendingConfirmationEmail, captcha.token ?? undefined);
      setResendState('sent');
    } catch (err) {
      setResendState('idle');
      setError(err instanceof Error ? err.message : 'Could not resend the confirmation email');
    } finally {
      captcha.reset(); // a token is single-use
    }
  };

  // ── Confirmation screen — after a signup that needs verification ──────────
  if (pendingConfirmationEmail) {
    return (
      <div className="w-full max-w-[380px]">
        <div className="mb-6 grid h-14 w-14 place-items-center rounded-2xl" style={{ background: 'rgba(255,255,255,.05)' }}>
          <Mail className="h-6 w-6" style={{ color: '#8ADAEB' }} />
        </div>
        <h1 className="mb-3 text-[30px] font-normal leading-tight text-white" style={{ fontFamily: DISPLAY }}>
          Confirm your email
        </h1>
        <p className="mb-6 text-[15px] leading-relaxed" style={{ color: MUTED }}>
          We sent a confirmation link to <span className="text-white">{pendingConfirmationEmail}</span>.
          Click it to activate your account — you can close this page, and the app
          will sign you in once you confirm.
        </p>

        {error && <ErrorNote>{error}</ErrorNote>}

        {resendState === 'sent' ? (
          <p className="mb-4 text-[14px]" style={{ color: '#8ADAEB' }}>Sent — check your inbox again.</p>
        ) : (
          <>
          <div className="mb-3">{captcha.widget}</div>
          <button
            type="button"
            onClick={handleResend}
            disabled={resendState === 'sending' || !captcha.ready}
            className="mb-4 flex h-[52px] w-full items-center justify-center gap-2.5 rounded-xl border text-[15px] transition-colors hover:bg-white/5 disabled:opacity-50"
            style={{ borderColor: 'rgba(255,255,255,.14)', color: '#FFFFFF' }}
          >
            {resendState === 'sending' && <Loader2 className="h-4 w-4 animate-spin" />}
            Resend confirmation email
          </button>
          </>
        )}

        <button
          type="button"
          onClick={() => { clearPendingConfirmation(); setResendState('idle'); setMode('signin'); }}
          className="inline-flex items-center gap-2 text-[14px] transition-colors hover:text-white"
          style={{ color: MUTED }}
        >
          <ArrowLeft className="h-4 w-4" />
          Back to sign in
        </button>
      </div>
    );
  }

  // ── Sign in / create account ─────────────────────────────────────────────
  const isSignup = mode === 'signup';

  return (
    <div className="w-full max-w-[380px]">
      <p className="mb-2 text-[11px] uppercase tracking-[.2em]" style={{ color: FAINT }}>
        {isSignup ? 'You have been approved' : 'Welcome back'}
      </p>
      <h1 className="mb-5 text-[30px] font-normal leading-tight text-white lg:mb-7 lg:text-[34px]" style={{ fontFamily: DISPLAY }}>
        {isSignup ? 'Create your account' : 'Log in'}
      </h1>

      <form onSubmit={handleSubmit} className="flex flex-col gap-3.5 lg:gap-4">
        {error && <ErrorNote>{error}</ErrorNote>}

        {isSignup && (
          <Field label="Preferred name">
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoComplete="given-name"
              placeholder="How you like to be addressed"
              className={INPUT}
              style={inputStyle}
            />
          </Field>
        )}

        <Field label="Email">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            placeholder="you@example.com"
            className={INPUT}
            style={inputStyle}
          />
        </Field>

        <Field label="Password">
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete={isSignup ? 'new-password' : 'current-password'}
            placeholder="••••••••"
            className={INPUT}
            style={inputStyle}
          />
        </Field>

        {captcha.widget}

        <button
          type="submit"
          disabled={loading || !captcha.ready}
          className="mt-1 flex h-[52px] w-full items-center justify-center gap-2.5 rounded-xl text-[15px] font-semibold transition-opacity disabled:opacity-60"
          style={{ background: 'linear-gradient(135deg, #3CC2DD, #A739E2)', color: '#04222B' }}
        >
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
          {isSignup ? 'Create account' : 'Log in'}
          {!loading && <ArrowRight className="h-4 w-4" />}
        </button>
      </form>

      {/* ⚠️⚠️ REQUEST EARLY ACCESS IS THE PROMINENT ROUTE, BUT CREATING AN ACCOUNT
          MUST STAY REACHABLE. The funnel is: request → approval email → "Create
          your account" → sign up. Replacing signup outright would have left every
          approved person with an invitation they could not use, which is the one
          way to break a funnel that was proved working end to end. So early
          access leads, and signup stays for the people already through it. */}
      <div className="mt-6 flex flex-col gap-3 border-t pt-5 lg:mt-7 lg:pt-6" style={{ borderColor: 'rgba(255,255,255,.08)' }}>
        {isSignup ? (
          <p className="text-[14px]" style={{ color: MUTED }}>
            Already have an account?{' '}
            <button type="button" onClick={() => setMode('signin')} className="text-white underline underline-offset-4">
              Log in
            </button>
          </p>
        ) : (
          <>
            <a
              href={EARLY_ACCESS_URL}
              className="flex h-[52px] w-full items-center justify-center gap-2.5 rounded-xl border text-[15px] transition-colors hover:bg-white/5"
              style={{ borderColor: 'rgba(255,255,255,.14)', color: '#FFFFFF' }}
            >
              Request early access
              <ArrowRight className="h-4 w-4" />
            </a>
            <p className="text-center text-[13px]" style={{ color: FAINT }}>
              Already approved?{' '}
              <button type="button" onClick={() => setMode('signup')} className="text-white underline underline-offset-4">
                Create your account
              </button>
            </p>
          </>
        )}
      </div>
    </div>
  );
}

// ── Shared bits ─────────────────────────────────────────────────────────────

const DISPLAY = "'Brother 1816', Outfit, system-ui, -apple-system, sans-serif";
const MUTED = '#9AA3B2';
const FAINT = '#6B7280';

const INPUT = 'h-[52px] w-full rounded-xl px-4 text-[15px] outline-none transition-colors';
const inputStyle: React.CSSProperties = {
  background: 'rgba(255,255,255,.04)',
  border: '1px solid rgba(255,255,255,.12)',
  color: '#FFFFFF',
  colorScheme: 'dark',
};

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[11px] uppercase tracking-[.14em]" style={{ color: MUTED }}>{label}</span>
      {children}
    </label>
  );
}

function ErrorNote({ children }: { children: React.ReactNode }) {
  return (
    <p
      role="alert"
      className="rounded-xl border px-4 py-3 text-[14px]"
      style={{ borderColor: 'rgba(244,63,94,.35)', background: 'rgba(244,63,94,.08)', color: '#FDA4AF' }}
    >
      {children}
    </p>
  );
}
